import BundleCards from "@components/Site/PricingPage/BundleCards"
import PlanPriceCards from "@components/Site/PricingPage/PlanPriceCards"
import BubblesBg from "@components/Site/BubblesBg/BubblesBg"
import Features from "@components/Site/PricingPage/Features"
import { useState, useEffect, useRef } from "react"
import ArrowMenuDesktop from "@components/Site/ArrowMenuDesktop"
import ArrowMenuMobile from "@components/Site/ArrowMenuMobile"
import Value from "aldoo-ra/Value"
import PlanDurationSwitcher from "@components/Site/PricingPage/PlanDurationSwitcher"
import AldooTransferBanner from "@components/AldooTransferBanner"

const Page = () => {
  const [isClient, setIsClient] = useState(false)
  const [activeCategory, setActiveCategory] = Value("plan_category")
  const [isDesktop, setIsDesktop] = useState(false)
  const [hasBundles, setHasBundles] = useState(true) // Default to true to show bundles menu item by default
  const [menuRefreshKey, setMenuRefreshKey] = useState(0)

  const userInitiatedScroll = useRef(false)
  const lastScrollTime = useRef(0)
  const containerRef = useRef(null)

  // Define the viewport width threshold for desktop/mobile menu
  const DESKTOP_THRESHOLD = 1280 // Common breakpoint for tablets and above

  const debounce = (func, wait) => {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  const scrollToCategory = (category) => {
    // Target the anchor element by its id
    const anchorElement = containerRef.current?.querySelector(
      `#${category}-anchor`
    )

    if (anchorElement) {
      userInitiatedScroll.current = true
      lastScrollTime.current = Date.now()

      const rect = anchorElement.getBoundingClientRect()
      // Use window.scrollY for modern browser support
      const scrollTop =
        window.scrollY || document.documentElement.scrollTop || 0

      // Define a fixed top margin (in pixels) to position the section below viewport top
      let topMargin = window.innerWidth < 1080 ? 20 : 120 // Adjust this value as needed

      if (category === "individual" || category === "family-and-business") {
        topMargin = window.innerWidth < 768 ? 80 : 80

        if (window.innerWidth < 1080 && window.innerWidth >= 768) {
          topMargin = 80
        }
        if (window.innerWidth >= 1080 && window.innerWidth < 1280) {
          topMargin = 160
        }
        if (window.innerWidth >= 1280) {
          topMargin = 180
        }
      }

      // Calculate the position to scroll to
      const scrollTo = scrollTop + rect.top - topMargin

      window.scrollTo({
        top: scrollTo,
        behavior: "smooth",
      })

      // Reset the user initiated scroll flag after animation completes
      setTimeout(() => {
        userInitiatedScroll.current = false
      }, 1000)
    }
  }

  useEffect(() => {
    setIsClient(true)

    if (typeof window === "undefined") return

    // Function to check and set the current view mode
    const checkViewportWidth = () => {
      setIsDesktop(window.innerWidth >= DESKTOP_THRESHOLD)
    }

    // Initial check
    checkViewportWidth()

    // Set up resize listener for responsive behavior
    const handleResize = debounce(checkViewportWidth, 100)
    window.addEventListener("resize", handleResize)

    const urlParams = new URLSearchParams(window.location.search)
    const plansParam = urlParams.get("plans")

    if (plansParam) {
      setActiveCategory(plansParam)

      setTimeout(() => {
        scrollToCategory(plansParam)
      }, 100)
    } else {
      // Set default category to "individual" if no category is specified
      setActiveCategory("individual")

      // Update URL to include the default category
      const url = new URL(window.location)
      url.searchParams.set("plans", "individual")
      window.history.replaceState({}, "", url.toString())

      // Optional: scroll to the individual section
      setTimeout(() => {
        scrollToCategory("individual")
      }, 100)
    }

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") return

    const handleScroll = () => {
      lastScrollTime.current = Date.now()

      // Only skip processing if we're in the middle of a programmatic scroll
      if (userInitiatedScroll.current) return

      if (containerRef.current) {
        // Get all section wrappers
        const sections = containerRef.current.querySelectorAll("section")

        // Get visible height of viewport
        const viewHeight = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight
        )

        // Calculate the position we consider the "active" point (1/3 down from top of viewport)
        const activePointY = viewHeight / 3

        // Find the section containing this point
        let activeSection = null

        // Default to first section if we're at the top
        if (window.scrollY < 100 && sections.length > 0) {
          activeSection = sections[0].querySelector("[data-category]")
        } else {
          // Find which section contains our active point
          for (const section of sections) {
            const rect = section.getBoundingClientRect()
            // If the section crosses our active point, it's our target
            if (rect.top <= activePointY && rect.bottom >= activePointY) {
              activeSection = section.querySelector("[data-category]")
              break
            }
          }

          // If we haven't found a section yet, check which one is closest to viewport top
          if (!activeSection && sections.length > 0) {
            let closestSection = null
            let minDistance = Infinity

            for (const section of sections) {
              const rect = section.getBoundingClientRect()
              const distance = Math.abs(rect.top)

              if (distance < minDistance) {
                minDistance = distance
                closestSection = section
              }
            }

            if (closestSection) {
              activeSection = closestSection.querySelector("[data-category]")
            }
          }
        }

        // Update the active category if we found a new section
        if (activeSection) {
          const category = activeSection.dataset.category

          if (category !== activeCategory) {
            setActiveCategory(category)

            const url = new URL(window.location)
            url.searchParams.set("plans", category)
            window.history.replaceState({}, "", url.toString())
          }
        }
      }
    }

    // Use a less aggressive debounce to ensure scroll events are processed more frequently
    const debouncedScroll = debounce(handleScroll, 50)
    window.addEventListener("scroll", debouncedScroll, { passive: true })

    return () => {
      window.removeEventListener("scroll", debouncedScroll)
    }
  }, [activeCategory])

  const handleCategoryChange = (index, selectedItem) => {
    if (!selectedItem || !selectedItem.content?.options?.id) return

    const category = selectedItem.content.options.id

    if (category === activeCategory) {
      return
    }

    setActiveCategory(category)

    const url = new URL(window.location)
    url.searchParams.set("plans", category)
    window.history.replaceState({}, "", url.toString())

    scrollToCategory(category)
  }

  // Handle bundle availability updates
  const handleBundlesUpdate = (bundlesAvailable) => {
    const previousHasBundles = hasBundles
    
    setHasBundles(bundlesAvailable)
    
    // Force menu refresh when bundle availability changes
    if (previousHasBundles !== bundlesAvailable) {
      setMenuRefreshKey(prev => prev + 1)
    }
    
    // If no bundles available and user is on bundles tab, redirect to individual
    if (!bundlesAvailable && activeCategory === "bundles") {
      setActiveCategory("individual")
      const url = new URL(window.location)
      url.searchParams.set("plans", "individual")
      window.history.replaceState({}, "", url.toString())
      setTimeout(() => {
        scrollToCategory("individual")
      }, 100)
    }
  }

  // Check if current URL has bundles section when hasBundles state changes
  useEffect(() => {
    if (!hasBundles && activeCategory === "bundles") {
      handleBundlesUpdate(false)
    }
  }, [hasBundles, activeCategory])

  const hiddenItemsArray = hasBundles ? [] : ["bundles"]
  
  const commonProps = {
    menuGroupName: "PlanCategories",
    onSelectionChange: handleCategoryChange,
    styleOverrides: {
      menu_item: "my-0",
    },
    defaultSelected: activeCategory,
    enableScrollNavigation: false,
    hiddenItems: hiddenItemsArray, // Hide bundles menu item when no bundles available
    refreshKey: menuRefreshKey, // Force refresh when bundle availability changes
  }


  if (!isClient) return null

  return (
    <div ref={containerRef} className="mb-[160px]">
      {isDesktop ? (
        <ArrowMenuDesktop
          key={`arrow-menu-${activeCategory}-${hasBundles}`}
          {...commonProps}
        />
      ) : (
        <ArrowMenuMobile
          key={`arrow-menu-${activeCategory}-${hasBundles}`}
          {...commonProps}
        />
      )}

      <PlanDurationSwitcher />

      {/* Add anchor divs for each section to improve scroll target detection */}
      <section className="relative">
        <div
          id="individual-anchor"
          data-category="individual"
          className="absolute top-0 h-4 w-full"
        ></div>
        <div className="mt-[120px]">
          <PlanPriceCards card_category="individual" />
        </div>
      </section>

      <section className="relative">
        <div
          id="family-and-business-anchor"
          data-category="family-and-business"
          className="absolute top-0 h-4 w-full"
        ></div>
        <div>
          <PlanPriceCards card_category="family-and-business" />
        </div>
      </section>

      <section className="relative">
        <div
          id="one-off-anchor"
          data-category="one-off"
          className="absolute top-0 h-4 w-full"
        ></div>
        <div>
          <PlanPriceCards card_category="one-off" />
        </div>
      </section>

      <AldooTransferBanner placement="pricing" />

      <section className="relative">
        <div
          id="bundles-anchor"
          data-category="bundles"
          className="absolute top-0 h-4 w-full"
          style={{ display: hasBundles ? 'block' : 'none' }}
        ></div>

        <div style={{ display: hasBundles ? 'block' : 'none' }}>
          <BundleCards onBundlesUpdate={handleBundlesUpdate} />
        </div>
      </section>

      {/* <Features /> */}

      <BubblesBg variant="variant3" isFixed={true} />
    </div>
  )
}

export default Page
